import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import useApiData from "../hooks/useApiData";

const fallbackBlogPage = {
  hero: {
    badge: "DVX Blog",
    title: "Training Insights",
    description:
      "Expert advice, training tips, and the latest in fitness science",
  },
  blogs: [],
};

export default function Blog() {
  const { data: pageData, loading } = useApiData("/blog", fallbackBlogPage);

  const posts = pageData.blogs || [];

  // helper to format date
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  return (
    <div
      data-testid="blog-page"
      className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300"
    >
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData.hero?.badge}
            </span>
            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {pageData.hero?.title}
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData.hero?.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* BLOGS */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading...
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts
                .filter((post) => post.is_published)
                .map((post, index) => (
                  <motion.div
                    key={post.slug}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/blog/${post.slug}`}>
                      <div
                        data-testid={`blog-post-${post.slug}`}
                        className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] h-full group overflow-hidden transition-colors duration-300"
                      >
                        {/* IMAGE */}
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 left-4">
                            <span className="bg-[var(--dvx-primary)]/90 text-white text-xs px-3 py-1 font-oswald uppercase tracking-wider">
                              Blog
                            </span>
                          </div>
                        </div>

                        {/* CONTENT */}
                        <div className="p-6">
                          <div className="flex items-center gap-4 mb-3 text-sm text-[var(--dvx-text-muted)]">
                            <span>DVX Team</span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDate(post.published_at)}
                            </span>
                          </div>

                          <h3 className="font-oswald text-xl font-bold text-[var(--dvx-text)] uppercase mb-3 group-hover:text-[var(--dvx-primary)] transition-colors">
                            {post.title}
                          </h3>

                          <p className="text-[var(--dvx-text-muted)] font-manrope text-sm line-clamp-3 mb-4">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-wider group-hover:translate-x-2 transition-transform">
                            Read More
                            <ArrowRight size={16} className="ml-2" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
